import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useSessionJwt } from '../../hooks/useSessionJwt';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { ChevronRight } from 'lucide-react';
import { palette, components, typography, iconSize } from '../../utils/designTokens';
import { LOADING_MESSAGE, GRAPH_SELECT_NODE_MESSAGE } from '../../utils/strings';
import { isInDemoMode, enhanceTraceSpansResponse, getDemoTraceDetections } from '../../services/demoDataService';
import {
  fetchTraceDetail,
  fetchTraceSpans,
  fetchTraceClassifiers,
} from '../../services/traceDetailApi';
import { useClassifierDefinitions } from '../../services/classifiersApi';
import { fetchProblemRca, runProblemRcaAndWait, normalizeProblemRca } from '../../services/problemRcaApi';
import { getDetectionLabel, formatSignalLabelFromKey } from '../../utils/signalUtils';
import TraceSignalsStrip from '../../components/TraceSignalsStrip';
import TraceSummaryBar from '../../components/TraceSummaryBar';
import AnomalyShapeRadars from '../../components/signals/AnomalyShapeRadars';
import TurnOutline from './TurnOutline';
import TraceTimeline from './TraceTimeline';
import GraphTab from './GraphTab';
import AgentGraphTab from './AgentGraphTab';
import SpanDetailsPanel from './components/SpanDetailsPanel';

const PROBLEM_RCA_SUPPORTED_CLASSIFIER_KEYS = new Set([
  'data_exfiltration',
  'error_detection',
  'hallucination',
  'loop_detection',
  'prompt_injection',
  'workflow_anomaly',
]);
const TRACE_DETAIL_RCA_RANGE = 'all-time';

const rcaRowKey = (row) => row?.id || `${row?.classifier_key || 'detection'}:${row?.classifier_version || 'v'}`;

const detectionDisplayName = (row) => {
  const label = getDetectionLabel(row);
  if (label) return label;
  const key = row?.classifier_key || row?.key || '';
  return key ? (formatSignalLabelFromKey(key) || key) : 'Detection';
};

const RcaSection = ({ title, children }) => (
  <section className="rounded border border-border-subtle bg-background-secondary p-3">
    <div className="mb-1 text-[11px] font-semibold uppercase text-text-muted">{title}</div>
    {children}
  </section>
);

function TraceClassifierRcaModal({ state, onClose, onSelectSpan }) {
  const { loading, error, message, data, row } = state;
  const evidence = Array.isArray(data?.evidence) ? data.evidence : [];
  const fixes = Array.isArray(data?.recommended_fixes) ? data.recommended_fixes : [];
  const why = Array.isArray(data?.why_it_happened) ? data.why_it_happened : [];
  const title = data?.title || `${detectionDisplayName(row)} RCA`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: palette.quality.overlayModal }}
    >
      <div
        className="max-h-[84vh] w-full max-w-3xl overflow-hidden rounded-lg border border-border-subtle bg-background-primary"
        style={{ boxShadow: palette.quality.shadowModal }}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-text-primary">{title}</div>
            <div className="mt-1 text-xs text-text-muted">
              {data?.confidence ? `${data.confidence} confidence` : loading ? 'Generating explanation' : 'Trace-level RCA'}
              {data?.rca_status ? ` / ${data.rca_status}` : ''}
            </div>
          </div>
          <button
            type="button"
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-primary hover:bg-background-tertiary"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="max-h-[72vh] space-y-3 overflow-y-auto p-4">
          {loading && (
            <div className="rounded border border-border-subtle bg-background-secondary p-3 text-sm text-text-muted">
              Running RCA for this detection...
            </div>
          )}

          {error && !loading && (
            <div className="rounded border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
              {error}
            </div>
          )}

          {message && !loading && !error && !data && (
            <div className="rounded border border-border-subtle bg-background-secondary p-3 text-sm text-text-muted">
              {message}
            </div>
          )}

          {data && !loading && !error && (
            <>
              <RcaSection title="Summary">
                <p className="text-sm leading-relaxed text-text-primary">{data.summary || 'No summary returned.'}</p>
              </RcaSection>

              <RcaSection title="Likely root cause">
                <p className="text-sm leading-relaxed text-text-primary">{data.root_cause || 'No root cause returned.'}</p>
              </RcaSection>

              {why.length > 0 && (
                <RcaSection title="Why it happened">
                  <ol className="space-y-1 text-sm text-text-primary">
                    {why.map((item, idx) => (
                      <li key={idx}>
                        <span className="mr-2 text-text-muted">{idx + 1}.</span>
                        {String(item)}
                      </li>
                    ))}
                  </ol>
                </RcaSection>
              )}

              {fixes.length > 0 && (
                <RcaSection title="Potential fixes">
                  <div className="space-y-2">
                    {fixes.map((fix, idx) => (
                      <div key={idx} className="rounded border border-border-subtle bg-background-primary px-3 py-2">
                        <div className="text-sm text-text-primary">{fix.fix || JSON.stringify(fix)}</div>
                        {fix.owner && <div className="mt-1 text-[11px] text-text-muted">{fix.owner}</div>}
                      </div>
                    ))}
                  </div>
                </RcaSection>
              )}

              {evidence.length > 0 && (
                <RcaSection title="Evidence">
                  <div className="space-y-2">
                    {evidence.slice(0, 6).map((item, idx) => (
                      <button
                        key={`${item.span_id || idx}:rca-evidence`}
                        type="button"
                        className="block w-full rounded border border-border-subtle bg-background-primary px-3 py-2 text-left hover:bg-background-tertiary"
                        onClick={() => item.span_id && onSelectSpan?.(item.span_id)}
                      >
                        {item.span_id && <div className="mb-1 font-mono text-[11px] text-accent-primary-base">{item.span_id}</div>}
                        <div className="text-sm text-text-primary">{item.claim || item.what_happened || JSON.stringify(item)}</div>
                      </button>
                    ))}
                  </div>
                </RcaSection>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function TraceViewer({
  traceId,
  appId,
  initialTrace = null,
  activeTab = 'outline',
  requestedSpanId = '',
  onSessionId,
  signalsPortalTarget,
  leftPanel = null,
  className = '',
}) {
  const location = useLocation();
  const { session } = useStytchMemberSession();
  const { selectedEnvironment } = useEnvironment();
  const { sessionId, getJwt } = useSessionJwt();
  const tenantId = session?.organization_id || null;
  const isDemo = isInDemoMode(tenantId, selectedEnvironment, appId);
  const { definitionsIndex } = useClassifierDefinitions(appId, {
    enabled: Boolean(appId && selectedEnvironment && getJwt() && !isDemo),
  });
  const requestedEvidenceSpanId = typeof requestedSpanId === 'string' && requestedSpanId
    ? requestedSpanId
    : typeof location.state?.evidenceSpanId === 'string'
      ? location.state.evidenceSpanId
    : '';

  // ── Trace detail (enriches summary bar) ─────────────────────────────────────
  const [traceDetail, setTraceDetail] = useState(null);
  const [traceDetailId, setTraceDetailId] = useState(null);

  // ── Span data ────────────────────────────────────────────────────────────────
  const [spans, setSpans] = useState([]);
  const [loadingSpans, setLoadingSpans] = useState(false);
  const [spansError, setSpansError] = useState(null);
  const [spansSessionId, setSpansSessionId] = useState(null);

  // ── Participants ─────────────────────────────────────────────────────────────
  const [metaParticipants, setMetaParticipants] = useState(null);

  // ── Trace detections ────────────────────────────────────────────────────────
  const [traceDetections, setTraceDetections] = useState([]);
  const [anomalyShapeOpen, setAnomalyShapeOpen] = useState(false);
  const [rcaModal, setRcaModal] = useState({ open: false, loading: false, error: null, message: null, data: null, row: null });
  const [rcaLoadingKey, setRcaLoadingKey] = useState('');
  const rcaAbortRef = useRef(null);

  // ── Span selection + panel ───────────────────────────────────────────────────
  const [selectedSpan, setSelectedSpan] = useState(null);
  const [detailsTab, setDetailsTab] = useState('outline');
  const [panelWidth, setPanelWidth] = useState(30);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const layoutRef = useRef(null);

  // ── Filter + expansion ───────────────────────────────────────────────────────
  const [filterMode, setFilterMode] = useState('summary');
  const [expandedPaths, setExpandedPaths] = useState(new Set());

  // ── Reset all state when traceId changes ────────────────────────────────────
  useEffect(() => {
    setTraceDetail(null);
    setTraceDetailId(null);
    setSpans([]);
    setLoadingSpans(Boolean(traceId));
    setSpansError(null);
    setMetaParticipants(null);
    setTraceDetections([]);
    setSelectedSpan(null);
    setDetailsTab('outline');
    setFilterMode('summary');
    setExpandedPaths(new Set());
    setSpansSessionId(null);
    rcaAbortRef.current?.abort();
    rcaAbortRef.current = null;
    setRcaLoadingKey('');
    setRcaModal({ open: false, loading: false, error: null, message: null, data: null, row: null });
  }, [traceId]);

  useEffect(() => () => {
    rcaAbortRef.current?.abort();
    rcaAbortRef.current = null;
  }, []);

  // ── Participants ─────────────────────────────────────────────────────────────
  const participants = useMemo(() => {
    if (metaParticipants !== null) return metaParticipants;
    const seen = new Map();
    (spans || []).forEach(span => {
      if (!span.service_id || !span.service_name) return;
      const key = `${span.service_id}:${span.environment || ''}`;
      if (!seen.has(key)) seen.set(key, { service_id: span.service_id, service_name: span.service_name, environment: span.environment || '' });
    });
    return [...seen.values()];
  }, [metaParticipants, spans]);

  const isMultiService = participants.length > 1;
  const isMultiEnv = useMemo(() => {
    const envs = new Set(participants.map(p => p.environment).filter(Boolean));
    return envs.size > 1;
  }, [participants]);

  const evidenceSpanIds = useMemo(() => {
    const ids = new Set();
    traceDetections.forEach((row) => {
      if (row?.status !== 'detected' && !row?.detected) return;
      (Array.isArray(row.evidence_span_ids) ? row.evidence_span_ids : []).forEach(id => ids.add(id));
      if (row.primary_span_id) ids.add(row.primary_span_id);
    });
    return ids;
  }, [traceDetections]);

  // ── traceFromSpans fallback ──────────────────────────────────────────────────
  const traceFromSpans = useMemo(() => {
    if (!traceId) return null;
    if (!Array.isArray(spans) || spans.length === 0) return { trace_id: traceId };

    let minStartMs = Number.POSITIVE_INFINITY;
    let maxEndMs = Number.NEGATIVE_INFINITY;
    let totalTokens = 0;
    let totalCost = 0;
    let sawSpanCost = false;
    let errorCount = 0;
    const models = new Set();

    spans.forEach((span) => {
      if (!span || typeof span !== 'object') return;
      const startMs = span.start_time ? Date.parse(span.start_time) : NaN;
      const endMs = span.end_time ? Date.parse(span.end_time) : NaN;
      if (Number.isFinite(startMs)) minStartMs = Math.min(minStartMs, startMs);
      if (Number.isFinite(endMs)) maxEndMs = Math.max(maxEndMs, endMs);
      const promptTokens = Number(span.prompt_tokens || 0);
      const completionTokens = Number(span.completion_tokens || 0);
      const cachedTokens = Number(span.cached_prompt_tokens || 0);
      const spanTokensRaw = span.llm_token_count_total;
      const spanTokens = Number.isFinite(Number(spanTokensRaw))
        ? Number(spanTokensRaw)
        : (promptTokens + completionTokens + cachedTokens);
      totalTokens += spanTokens;
      // The v2 API returns no per-span cost under any name, so this never contributed and the
      // fallback reported cost 0 — a real-looking "$0.00" rather than "unknown". Track whether
      // anything actually supplied a cost so we can report null instead (console/src/CLAUDE.md:
      // use null, not 0, for missing data, so the UI can show "-").
      const spanCost = Number(span.cost ?? span.llm_cost);
      if (Number.isFinite(spanCost)) {
        totalCost += spanCost;
        sawSpanCost = true;
      }
      if (span.status_code === 'ERROR' || span.metrics?.has_error) errorCount += 1;
      if (span.llm_model_name) models.add(span.llm_model_name);
    });

    const hasValidTimeRange = Number.isFinite(minStartMs) && Number.isFinite(maxEndMs) && maxEndMs >= minStartMs;
    return {
      trace_id: traceId,
      span_count: spans.length,
      duration_ms: hasValidTimeRange ? maxEndMs - minStartMs : null,
      trace_start: Number.isFinite(minStartMs) ? new Date(minStartMs).toISOString() : null,
      tokens: totalTokens,
      cost: sawSpanCost ? totalCost : null,
      errors: errorCount,
      models: [...models],
    };
  }, [traceId, spans]);

  // ── normalizeTraceIo ─────────────────────────────────────────────────────────
  const normalizeTraceIo = useCallback((value, previewValue, fallbackSpanKind = 'CHAIN') => {
    if (value && typeof value === 'object') {
      const text = typeof value.text === 'string' ? value.text : null;
      if (text && text.trim().length > 0) {
        return { ...value, span_kind: value.span_kind || fallbackSpanKind };
      }
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return { text: value, span_kind: fallbackSpanKind };
    }
    if (typeof previewValue === 'string' && previewValue.trim().length > 0) {
      return { text: previewValue, span_kind: fallbackSpanKind };
    }
    return null;
  }, []);

  // ── summaryTrace ─────────────────────────────────────────────────────────────
  const summaryTrace = useMemo(() => {
    // Only use traceDetail when it was fetched for the current traceId —
    // prevents the previous trace's detail from flashing through during transition.
    const matchedDetail = traceDetailId === traceId ? traceDetail : null;
    const currentTrace = matchedDetail || initialTrace;
    if (!currentTrace && !traceFromSpans) return null;

    const baseTrace = currentTrace || {};
    const fallbackTrace = traceFromSpans || {};
    // initialTrace (list row) carries reliable input/output; traceDetail may set them to null
    // when first_user_input is absent (normalizeV2TraceDetail). Always prefer initialTrace first.
    const initial = initialTrace || {};
    const input = normalizeTraceIo(
      initial.input ?? baseTrace.input ?? fallbackTrace.input,
      initial.input_preview ?? baseTrace.input_preview ?? fallbackTrace.input_preview,
      initial.input?.span_kind || baseTrace.input?.span_kind || fallbackTrace.input?.span_kind || 'CHAIN'
    );
    const output = normalizeTraceIo(
      initial.output ?? baseTrace.output ?? fallbackTrace.output,
      initial.output_preview ?? baseTrace.output_preview ?? fallbackTrace.output_preview,
      initial.output?.span_kind || baseTrace.output?.span_kind || fallbackTrace.output?.span_kind || 'CHAIN'
    );

    // workflow_name comes from the detail API (trace_workflows_v2); the list API reads a
    // different table (trace_workflows v1) and can return a stale/different value.
    // Only show workflow once traceDetail is confirmed for this traceId.
    const workflowObj = matchedDetail ? (baseTrace.workflow || null) : null;
    const workflowName = matchedDetail
      ? (baseTrace.workflow_name || baseTrace.workflow?.workflow_name || baseTrace.workflow_id || null)
      : null;

    return {
      ...fallbackTrace,
      ...baseTrace,
      trace_id: baseTrace.trace_id || fallbackTrace.trace_id || traceId,
      session_id: baseTrace.session_id || baseTrace.sessionId || fallbackTrace.session_id || spansSessionId || null,
      trace_start: baseTrace.trace_start || baseTrace.start_time || fallbackTrace.trace_start || null,
      duration_ms: baseTrace.duration_ms ?? fallbackTrace.duration_ms ?? null,
      span_count: baseTrace.span_count ?? fallbackTrace.span_count ?? null,
      tokens: baseTrace.tokens ?? fallbackTrace.tokens ?? 0,
      total_input_tokens: baseTrace.total_input_tokens ?? fallbackTrace.total_input_tokens ?? null,
      total_output_tokens: baseTrace.total_output_tokens ?? fallbackTrace.total_output_tokens ?? null,
      total_cached_tokens: baseTrace.total_cached_tokens ?? fallbackTrace.total_cached_tokens ?? null,
      cost: baseTrace.cost ?? fallbackTrace.cost ?? null,
      errors: baseTrace.errors ?? baseTrace.error_count ?? fallbackTrace.errors ?? 0,
      models: Array.isArray(baseTrace.models) && baseTrace.models.length > 0
        ? baseTrace.models
        : (Array.isArray(fallbackTrace.models) ? fallbackTrace.models : []),
      workflows: Array.isArray(baseTrace.workflows)
        ? baseTrace.workflows
        : (Array.isArray(fallbackTrace.workflows) ? fallbackTrace.workflows : []),
      workflow: workflowObj,
      workflow_name: workflowName,
      input,
      output,
    };
  }, [traceDetail, traceDetailId, initialTrace, traceFromSpans, traceId, spansSessionId, normalizeTraceIo]);

  // ── Surface session_id to parent ─────────────────────────────────────────────
  useEffect(() => {
    const sid = summaryTrace?.session_id || spansSessionId || null;
    onSessionId?.(sid);
  }, [summaryTrace?.session_id, spansSessionId, onSessionId]);

  // ── Fetch trace detail ───────────────────────────────────────────────────────
  useEffect(() => {
    const jwt = getJwt();
    if (!traceId || !appId || !selectedEnvironment || !jwt || isDemo) return undefined;
    const abortController = new AbortController();
    (async () => {
      try {
        const trace = await fetchTraceDetail(traceId, appId, selectedEnvironment, jwt, { signal: abortController.signal });
        if (!abortController.signal.aborted && trace) {
          setTraceDetail(trace);
          setTraceDetailId(traceId);
        }
      } catch (err) {
        if (err?.name !== 'AbortError') console.warn('TraceViewer: trace detail fetch failed', err);
      }
    })();
    return () => abortController.abort();
  }, [traceId, appId, selectedEnvironment, sessionId, getJwt, isDemo]);

  // ── Fetch spans + classifiers ────────────────────────────────────────────────
  useEffect(() => {
    const jwt = getJwt();
    if (!traceId || !appId || !selectedEnvironment || !jwt) return undefined;
    // Demo apps have no backend service: read spans and signals from the mock layer.
    if (isDemo) {
      const data = enhanceTraceSpansResponse(null, tenantId, selectedEnvironment, appId, traceId);
      const demoSpans = data?.spans || [];
      setSpans(demoSpans);
      if (requestedEvidenceSpanId) {
        const requestedSpan = demoSpans.find(span => span?.span_id === requestedEvidenceSpanId);
        if (requestedSpan) setSelectedSpan(requestedSpan);
      }
      setTraceDetections(getDemoTraceDetections(traceId));
      setLoadingSpans(false);
      return undefined;
    }
    const abortController = new AbortController();
    (async () => {
      try {
        setLoadingSpans(true);
        setSpansError(null);

        const [rawData, classifierRows] = await Promise.all([
          fetchTraceSpans(traceId, appId, selectedEnvironment, jwt, { signal: abortController.signal }),
          fetchTraceClassifiers(traceId, appId, selectedEnvironment, jwt, { signal: abortController.signal }),
        ]);

        if (abortController.signal.aborted) return;

        const data = enhanceTraceSpansResponse(rawData, tenantId, selectedEnvironment, appId, traceId);
        const fetchedSpans = data.spans || [];
        setSpans(fetchedSpans);
        if (requestedEvidenceSpanId) {
          const requestedSpan = fetchedSpans.find(span => span?.span_id === requestedEvidenceSpanId);
          if (requestedSpan) setSelectedSpan(requestedSpan);
        }
        if (data.session_id) setSpansSessionId(data.session_id);
        setMetaParticipants(Array.isArray(data.meta?.participants) ? data.meta.participants : null);

        setTraceDetections(classifierRows);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('TraceViewer: failed to fetch spans:', err);
        setSpansError(err.message);
      } finally {
        if (!abortController.signal.aborted) setLoadingSpans(false);
      }
    })();
    return () => abortController.abort();
  }, [traceId, appId, selectedEnvironment, sessionId, getJwt, tenantId, isDemo, requestedEvidenceSpanId]);

  // ── filterMode resets expanded paths ────────────────────────────────────────
  useEffect(() => {
    setExpandedPaths(prev => prev.size === 0 ? prev : new Set());
  }, [filterMode]);

  // ── Resizable panel ──────────────────────────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current || !layoutRef.current) return;
    const containerRect = layoutRef.current.getBoundingClientRect();
    const newWidth = ((containerRect.right - e.clientX) / containerRect.width) * 100;
    setPanelWidth(Math.min(Math.max(newWidth, 15), 50));
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleMouseDown = useCallback(() => {
    isDraggingRef.current = true;
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ── Evidence selection ───────────────────────────────────────────────────────
  const handleSelectEvidenceSpan = useCallback((spanId) => {
    if (!spanId) return;
    const span = spans.find(s => s.span_id === spanId);
    if (span) setSelectedSpan(span);
  }, [spans]);

  const closeRcaModal = useCallback(() => {
    rcaAbortRef.current?.abort();
    rcaAbortRef.current = null;
    setRcaLoadingKey('');
    setRcaModal({ open: false, loading: false, error: null, data: null, row: null });
  }, []);

  const handleDetectionRca = useCallback(async (row) => {
    const classifierKey = row?.classifier_key;
    const jwt = getJwt();
    if (!classifierKey || !PROBLEM_RCA_SUPPORTED_CLASSIFIER_KEYS.has(classifierKey) || !traceId || !jwt) return;

    rcaAbortRef.current?.abort();
    const abortController = new AbortController();
    rcaAbortRef.current = abortController;
    const rowKey = rcaRowKey(row);
    setRcaLoadingKey(rowKey);
    setRcaModal({ open: true, loading: true, error: null, message: null, data: null, row });

    try {
      const problemId = row?.problem_id || '';
      if (!problemId) {
        throw new Error('Problem assignment is still pending for this detection.');
      }

      const latestRcaId = row?.latest_rca_id || '';
      let data = null;
      if (latestRcaId) {
        data = await fetchProblemRca(
          appId,
          selectedEnvironment,
          TRACE_DETAIL_RCA_RANGE,
          latestRcaId,
          jwt,
          { signal: abortController.signal },
        );
      } else {
        const result = await runProblemRcaAndWait(
          appId,
          selectedEnvironment,
          problemId,
          jwt,
          { signal: abortController.signal },
        );
        if (result?.timed_out) {
          if (abortController.signal.aborted) return;
          setRcaModal({
            open: true,
            loading: false,
            error: null,
            message: 'RCA is still running. Refresh status in a moment.',
            data: null,
            row,
          });
          return;
        }
        data = result?.rca || null;
      }
      if (abortController.signal.aborted) return;
      setRcaModal({ open: true, loading: false, error: null, message: null, data: normalizeProblemRca(data), row });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setRcaModal({ open: true, loading: false, error: err?.message || 'Failed to load RCA', message: null, data: null, row });
    } finally {
      if (rcaAbortRef.current === abortController) {
        rcaAbortRef.current = null;
        setRcaLoadingKey('');
      }
    }
  }, [appId, selectedEnvironment, getJwt, traceId]);

  // Business Task Anomaly shape: demo only. The real detector keeps just its top five
  // deviations, so only the demo's signal row carries the all_deviations the radars need.
  const anomalyShapeRuns = useMemo(() => {
    if (!isDemo) return [];
    const row = traceDetections.find(r => r?.classifier_key === 'workflow_anomaly' && (r.status === 'detected' || r.detected));
    const deviations = row?.details?.all_deviations;
    return Array.isArray(deviations) && deviations.length > 0 ? [{ name: 'This run', all_deviations: deviations }] : [];
  }, [isDemo, traceDetections]);

  // ── Render ───────────────────────────────────────────────────────────────────
  const signalsStrip = (
    <TraceSignalsStrip
      detections={traceDetections}
      definitionsIndex={definitionsIndex}
      onEvidenceClick={handleSelectEvidenceSpan}
      onRcaClick={isDemo ? undefined : handleDetectionRca}
      rcaEnabledKeys={PROBLEM_RCA_SUPPORTED_CLASSIFIER_KEYS}
      rcaLoadingKey={rcaLoadingKey}
    />
  );
  const hasSignalsPortalTarget = signalsPortalTarget !== undefined;

  return (
    <div className={`flex min-h-0 flex-1 flex-col gap-4 ${className}`}>
      {hasSignalsPortalTarget
        ? (signalsPortalTarget ? createPortal(signalsStrip, signalsPortalTarget) : null)
        : signalsStrip}

      <TraceSummaryBar trace={summaryTrace} />

      {anomalyShapeRuns.length > 0 && (
        <section className={`${components.card.rounded} ${components.card.border} ${components.card.bg} ${components.card.padding}`}>
          <button
            type="button"
            className="group flex w-full cursor-pointer items-center gap-2 text-left"
            aria-expanded={anomalyShapeOpen}
            onClick={() => setAnomalyShapeOpen(open => !open)}
          >
            <ChevronRight
              className={`${iconSize.sm} shrink-0 transition-transform ${anomalyShapeOpen ? 'rotate-90' : ''}`}
              style={{ color: palette.text.supporting }}
              aria-hidden="true"
            />
            <span className={`${typography.size.sm} ${typography.weight.semibold}`} style={{ color: palette.text.primary }}>
              Business Task Anomaly shape
            </span>
            <span className={typography.size.xs} style={{ color: palette.text.muted }}>
              Workload and execution against this task's baseline
            </span>
            <span
              className={`ml-auto rounded border px-2 py-0.5 ${typography.size.xs} ${typography.weight.medium} group-hover:opacity-80`}
              style={{ color: palette.accents.primary.base, borderColor: palette.borders.medium }}
            >
              {anomalyShapeOpen ? 'Hide chart' : 'Show chart'}
            </span>
          </button>
          {anomalyShapeOpen && (
            <div className={components.card.divider}>
              <AnomalyShapeRadars runs={anomalyShapeRuns} />
            </div>
          )}
        </section>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-accent-lavender/20 bg-background-secondary">
        <div ref={layoutRef} className="flex h-full w-full">
          {leftPanel}

          {/* Center panel */}
          <div className="bg-background-primary rounded-lg border border-border-subtle overflow-hidden relative flex-1 min-w-0">
            {loadingSpans ? (
              <div className={components.table.empty}>{LOADING_MESSAGE}</div>
            ) : spansError ? (
              <div className="bg-danger/10 border border-danger/20 rounded-lg p-4 m-4">
                <p className="text-danger text-sm">Error loading trace data: {spansError}</p>
              </div>
            ) : (
              <>
                {activeTab === 'graph' && (
                  <GraphTab
                    traceId={traceId}
                    spans={spans}
                    selectedSpan={selectedSpan}
                    onSpanSelect={setSelectedSpan}
                    filterMode={filterMode}
                    onFilterModeChange={setFilterMode}
                    participants={participants}
                  />
                )}
                {activeTab === 'timeline' && (
                  <TraceTimeline
                    traceId={traceId}
                    spans={spans}
                    trace={summaryTrace}
                    selectedSpan={selectedSpan}
                    onSpanSelect={setSelectedSpan}
                    expandedPaths={expandedPaths}
                    onExpandedPathsChange={setExpandedPaths}
                    filterMode={filterMode}
                    onFilterModeChange={setFilterMode}
                    isMultiService={isMultiService}
                    isMultiEnv={isMultiEnv}
                    evidenceSpanIds={evidenceSpanIds}
                    participants={participants}
                  />
                )}
                {activeTab === 'outline' && (
                  <TurnOutline
                    spans={spans}
                    selectedSpan={selectedSpan}
                    onSpanSelect={setSelectedSpan}
                    trace={summaryTrace}
                    traceDetections={traceDetections}
                    filterMode={filterMode}
                    onFilterModeChange={setFilterMode}
                    isMultiService={isMultiService}
                    isMultiEnv={isMultiEnv}
                    evidenceSpanIds={evidenceSpanIds}
                    participants={participants}
                  />
                )}
                {activeTab === 'agents' && (
                  <AgentGraphTab
                    traceId={traceId}
                    appId={appId}
                    env={selectedEnvironment}
                   
                    spans={spans}
                    selectedSpan={selectedSpan}
                    onSpanSelect={setSelectedSpan}
                  />
                )}
              </>
            )}
          </div>

          {/* Resize divider */}
          <div
            className="w-1 hover:w-2 transition-all cursor-col-resize flex items-center justify-center group"
            style={{ backgroundColor: isDragging ? palette.accents.primary.base : palette.borders.subtle }}
            onMouseDown={handleMouseDown}
          >
            <div
              className="w-0.5 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ backgroundColor: palette.accents.primary.base }}
            />
          </div>

          {/* Right panel — SpanDetailsPanel */}
          <div
            className="bg-background-tertiary rounded-lg border p-4 flex flex-col shrink-0 overflow-auto"
            style={{ width: `${panelWidth}%`, minWidth: '250px', borderColor: palette.borders.medium }}
          >
            {selectedSpan ? (
              <SpanDetailsPanel
                span={selectedSpan}
                spans={spans}
                traceDetections={traceDetections}
                activeTab={detailsTab}
                onTabChange={setDetailsTab}
                traceId={traceId}
                selectedApp={appId}
              />
            ) : (
              <div className="text-sm text-text-muted">{GRAPH_SELECT_NODE_MESSAGE}</div>
            )}
          </div>
        </div>
      </div>

      {rcaModal.open && (
        <TraceClassifierRcaModal
          state={rcaModal}
          onClose={closeRcaModal}
          onSelectSpan={(spanId) => {
            handleSelectEvidenceSpan(spanId);
            closeRcaModal();
          }}
        />
      )}
    </div>
  );
}
